import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import { useParams } from "react-router-dom";
import { PackagePlus, RefreshCcw } from "lucide-react";
import TransactionAttachments from "../components/TransactionAttachments";
import "../Product/customers.css";

function money(n) {
  return `₦${Number(n || 0).toLocaleString()}`;
}

export default function SaleDetail() {
  const { companyId } = useCompany();
  const { id } = useParams();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [hdr, setHdr] = useState(null);
  const [lines, setLines] = useState([]);

  const [warehouses, setWarehouses] = useState([]);

  async function load() {
    if (!companyId || !id) return;
    setErr("");

    const { data: h, error: hErr } = await supabase
      .from("sales_transactions")
      .select("*, customers(name,company_name)")
      .eq("company_id", companyId)
      .eq("id", id)
      .single();

    if (hErr) {
      setErr(hErr.message);
      setHdr(null);
      return;
    }
    setHdr(h || null);

    const { data: l, error: lErr } = await supabase
      .from("sales_transaction_lines")
      .select("*, inventory_items(id,sku,name,unit)")
      .eq("company_id", companyId)
      .eq("sales_transaction_id", id)
      .order("created_at", { ascending: true });

    if (lErr) {
      setErr(lErr.message);
      setLines([]);
      return;
    }
    setLines(l || []);

    // warehouses (for default fallback)
    const { data: wh, error: whErr } = await supabase
      .from("warehouses")
      .select("id,name,is_default")
      .eq("company_id", companyId)
      .order("name", { ascending: true });

    if (!whErr) setWarehouses(wh || []);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, id]);

  const invLines = useMemo(() => {
    // Only lines linked to an inventory item should affect stock.
    // Support both: line.inventory_item_id OR line.item_id OR line.inventory_items?.id
    return (lines || [])
      .map((l) => {
        const itemId =
          l.inventory_item_id || l.item_id || l.inventory_items?.id || null;
        const qty = Number(l.quantity || 0);
        return {
          raw: l,
          itemId,
          qty,
        };
      })
      .filter((x) => x.itemId && x.qty > 0);
  }, [lines]);

  const canPostInventory = useMemo(() => {
    if (!hdr) return false;
    if (hdr.posted_to_inventory) return false;
    return invLines.length > 0;
  }, [hdr, invLines.length]);

  function pickWarehouseId() {
    // Prefer transaction warehouse if present, else default warehouse
    const txWh = hdr?.warehouse_id;
    if (txWh) return txWh;

    const def = (warehouses || []).find((w) => w.is_default);
    return def?.id || "";
  }

  async function postInventory() {
    setErr("");
    if (!companyId || !hdr?.id) return;

    const warehouseId = pickWarehouseId();
    if (!warehouseId) {
      setErr("No warehouse found. Please create a default warehouse first.");
      return;
    }

    // Build movement lines payload
    // For Stock OUT, unit_cost is not required by your Edge validation
    const payloadLines = invLines.map((x) => ({
      item_id: x.itemId,
      quantity: x.qty,
      unit_cost: null,
      direction: null,
    }));

    setBusy(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/inventory-post-movement`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company_id: companyId,
          movement_type: "OUT",
          movement_date: hdr.sale_date || new Date().toISOString().slice(0, 10),
          warehouse_id: warehouseId,
          warehouse_to_id: null,
          notes: `Auto stock OUT from Sale ${hdr.id}`,
          reference_type: "sale",
          reference_id: hdr.id,
          post_to_journal: true,
          lines: payloadLines,
        }),
      });

      const raw = await res.text();
      let json = {};
      try {
        json = raw ? JSON.parse(raw) : {};
      } catch {
        json = { error: raw || "Unknown error" };
      }

      if (!res.ok) throw new Error(json?.error || "Failed to post inventory.");

      // Mark sale as posted to inventory (prevents double posting)
      const { error: upErr } = await supabase
        .from("sales_transactions")
        .update({ posted_to_inventory: true })
        .eq("company_id", companyId)
        .eq("id", hdr.id);

      if (upErr) {
        // Movement posted but flag failed: show warning not fatal
        setErr(
          `Inventory posted, but could not update posted_to_inventory flag: ${upErr.message}`
        );
      } else {
        await load();
      }
    } catch (e) {
      setErr(e?.message || "Failed to post inventory.");
    } finally {
      setBusy(false);
    }
  }

  if (!hdr)
    return (
      <div className="customers-container">
        {err && <div className="banner error">{err}</div>}
        <div className="panel" style={{ padding: 14 }}>
          Loading…
        </div>
      </div>
    );

  return (
    <div className="sale-detail-page">
      {err && <div className="sale-detail-alert error">{err}</div>}

      <header className="sale-detail-hero">
        <div>
          <p className="sale-detail-eyebrow">Sales Transaction</p>

          <h1>Sale Details</h1>

          <p>
            {hdr.sale_date || "—"} · {hdr.customers?.name || "—"} ·{" "}
            {hdr.payment_status || "—"}
            {hdr.posted_to_inventory ? " · Inventory posted" : ""}
          </p>
        </div>

        <div className="sale-detail-actions">
          <div className="sale-detail-total">
            <span>Total Sale</span>
            <strong>{money(hdr.total)}</strong>
          </div>

          <button
            className="sale-detail-btn soft"
            onClick={load}
            disabled={busy}
          >
            <RefreshCcw size={16} />
            Refresh
          </button>

          {canPostInventory && (
            <button
              className="sale-detail-btn primary"
              onClick={postInventory}
              disabled={busy}
              title="Post inventory movement for stock items on this sale"
            >
              <PackagePlus size={16} />
              {busy ? "Posting…" : "Post Inventory"}
            </button>
          )}
        </div>
      </header>

      <section className="sale-detail-summary-grid">
        <div className="sale-detail-summary-card">
          <span>Customer</span>
          <strong>{hdr.customers?.name || "—"}</strong>
        </div>

        <div className="sale-detail-summary-card">
          <span>Sale Date</span>
          <strong>{hdr.sale_date || "—"}</strong>
        </div>

        <div className="sale-detail-summary-card">
          <span>Payment Status</span>
          <strong>{hdr.payment_status || "—"}</strong>
        </div>

        <div className="sale-detail-summary-card">
          <span>Inventory Status</span>
          <strong>
            {hdr.posted_to_inventory
              ? "Posted"
              : invLines.length > 0
              ? "Pending"
              : "Not applicable"}
          </strong>
        </div>
      </section>

      {!canPostInventory && invLines.length > 0 && hdr.posted_to_inventory && (
        <div className="sale-detail-note">
          Inventory movement has already been posted for this sale.
        </div>
      )}

      {invLines.length === 0 && (
        <div className="sale-detail-note">
          This sale has no inventory items. Only manual/service lines were
          recorded.
        </div>
      )}

      <section className="sale-detail-panel">
        <div className="sale-detail-panel-head">
          <div>
            <h3>Sale Line Items</h3>
            <p>Review products, services, quantities and line totals.</p>
          </div>
        </div>

        <div className="sale-detail-table-wrap">
          <table className="sale-detail-table">
            <thead>
              <tr>
                <th>Item / Description</th>
                <th className="num">Qty</th>
                <th className="num">Unit Price</th>
                <th className="num">Line Total</th>
              </tr>
            </thead>

            <tbody>
              {lines.map((l) => (
                <tr key={l.id}>
                  <td>
                    <div className="sale-detail-item">
                      <strong>
                        {l.inventory_items
                          ? `${l.inventory_items.sku} - ${l.inventory_items.name}`
                          : l.description || "—"}
                      </strong>

                      <span>
                        {l.inventory_items
                          ? "Inventory item"
                          : "Manual/service line"}
                      </span>
                    </div>
                  </td>

                  <td className="num">{Number(l.quantity || 0)}</td>
                  <td className="num">{money(l.unit_price)}</td>
                  <td className="num">
                    <strong>{money(l.line_total)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!lines.length && (
            <div className="sale-detail-empty">
              No sale line items found for this transaction.
            </div>
          )}
        </div>
      </section>

      <section className="sale-detail-panel">
        <div className="sale-detail-panel-head">
          <div>
            <h3>Attachments</h3>
            <p>Upload or review supporting documents for this sale.</p>
          </div>
        </div>

        <TransactionAttachments
          companyId={companyId}
          entityType="sale"
          entityId={id}
        />
      </section>
    </div>
  );
}
